import React, { useEffect, useMemo, useState } from 'react'
import { BarChart } from '@mui/x-charts/BarChart'
import { Box, Skeleton, Alert } from '@mui/material'
import { api } from '../../../../services/Main/Api'
import { getCondoId } from '../../../../services/Main/AuthStorage'

const brl = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
})

const percentFmt = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
})

const formatBRLFromCents = (valueInCents) =>
    brl.format(Number(valueInCents || 0) / 100)

// Eixo Y -> compacto (k/M) em reais.
const yAxisValueFormatter = (valueInCents) => {
    const valueInReais = Number(valueInCents || 0) / 100
    const abs = Math.abs(valueInReais)

    if (abs >= 1_000_000) {
        return `${(valueInReais / 1_000_000).toFixed(1).replace('.', ',')}M`
    }
    if (abs >= 1_000) {
        return `${(valueInReais / 1_000).toFixed(1).replace('.', ',')}k`
    }
    return `${Math.round(valueInReais)}`
}

const chartSetting = { height: 400 }

export default function TransactionsTotalLast3YearsChart() {
    const [years, setYears] = useState([])
    const [totals, setTotals] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        let alive = true

        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')

                const condoId = getCondoId?.()
                const params = {}
                // if (condoId) params.condo_id = condoId

                const res = await api.get('analytics/transactions-total-3y/', { params })

                if (!alive) return

                setYears(res?.data?.years ?? [])
                setTotals(res?.data?.totals ?? {})
            } catch (e) {
                if (!alive) return
                setError('Não foi possível carregar o total de movimentação dos últimos 3 anos.')
                setYears([])
                setTotals({})
            } finally {
                if (!alive) return
                setLoading(false)
            }
        }

        fetchData()
        return () => {
            alive = false
        }
    }, [])

    const dataset = useMemo(() => {
        if (!years?.length) return []
        return years.map((y) => ({
            year: String(y),
            value: Number(totals?.[`year${y}`] ?? 0),
        }))
    }, [years, totals])

    const series = useMemo(() => {
        if (!years?.length) return []

        const sortedYears = [...years].sort((a, b) => a - b)
        const prevByYear = new Map()
        sortedYears.forEach((y, idx) => {
            prevByYear.set(y, idx > 0 ? sortedYears[idx - 1] : null)
        })

        return [
            {
                dataKey: 'value',
                label: 'Total',
                color: '#1565c0', // 🎨 cor ajustada aqui
                valueFormatter: (valueInCents, context) => {
                    const valueText = formatBRLFromCents(valueInCents)
                    const row = dataset?.[context?.dataIndex]
                    const currYear = Number(row?.year)
                    const prevYear = prevByYear.get(currYear)

                    if (!prevYear) return valueText

                    const baseCents = Number(totals?.[`year${prevYear}`] ?? 0)
                    const currCents = Number(totals?.[`year${currYear}`] ?? 0)

                    if (baseCents <= 0) return `${valueText} (—)`

                    const growth = (currCents - baseCents) / baseCents
                    const sign = growth >= 0 ? '+' : ''
                    const pctText = `${sign}${percentFmt.format(growth * 100)}%`

                    return `${valueText} (${pctText})`
                },
            },
        ]
    }, [years, totals, dataset])

    if (loading) {
        return (
            <Box>
                <Skeleton variant="rounded" height={300} />
            </Box>
        )
    }

    if (error) return <Alert severity="warning">{error}</Alert>

    if (!dataset?.length) {
        return <Alert severity="info">Sem dados para exibir no período.</Alert>
    }

    return (
        <BarChart
            dataset={dataset}
            xAxis={[{ dataKey: 'year', scaleType: 'band' }]}
            yAxis={[{ valueFormatter: yAxisValueFormatter }]}
            series={series}
            {...chartSetting}
        />
    )
}
